// @flow
import * as React from 'react';

import assertions from 'lib/assertions';
import Avatar from 'components/Avatar.jsx';
import Button from 'components/Button.jsx';
import Checkbox from 'components/Checkbox.jsx';
import Container from 'components/Container.jsx';
import Field from 'components/Field.jsx';
import FormElement from 'components/FormElement.jsx';
import Heading from 'components/Heading.jsx';
import Input from 'components/Input.jsx';
import Label from 'components/Label.jsx';
import Link from 'components/Link.jsx';
import MediaObject from 'components/MediaObject.jsx';
import Row from 'components/Row.jsx';
import Select from 'components/Select.jsx';
import Text from 'components/Text.jsx';
import TextWithGlyph from 'components/TextWithGlyph.jsx';
import Textarea from 'components/Textarea.jsx';
import ToastActions from 'actions/ToastActions.js';
import View from 'components/View.jsx';
import WizardPageLayout from 'components/WizardPageLayout';

import type {
  GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_messageContent,
  GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_sender,
} from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageWithoutSends';
import type { AdminNewAutoMessageOrgs_organizations } from './queries/__generated__/AdminNewAutoMessageOrgs';

type Props = {
  messageName: ?string,
  orgUuid: ?string,
  sender: ?GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_sender,
  includeParents: boolean,
  includeStudents: boolean,
  messageContent: ?GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_messageContent,
  potentialOrgs: AdminNewAutoMessageOrgs_organizations[],
  onChangeField: () => void,
  onAdvance: () => void,
  onBack: () => void,
  onSave: (fields: SaveAutoMessageInput, onSuccess: Function) => void,
};

type State = {
  messageNameFromField: ?string,
  orgUuidFromField: ?string,
  senderFromField: ?AutoMessageSenderInput,
  includeParentsFromField: boolean,
  includeStudentsFromField: boolean,
  messageContentFromField: ?AutoMessageContentInput,
};

class AdminNewAutoMessageCompose extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      messageNameFromField: props.messageName,
      orgUuidFromField: props.orgUuid,
      senderFromField: props.sender && {
        uuid: props.sender.uuid,
        type: props.sender.__typename === 'UserSender' ? 'user' : 'district',
      },
      includeParentsFromField: props.includeParents || false,
      includeStudentsFromField: props.includeStudents || false,
      messageContentFromField: props.messageContent && { body: props.messageContent.body },
    };
  }

  handleMessageNameChange = (messageName: ?string) => {
    this.setState({ messageNameFromField: messageName });
    this.props.onChangeField();
  };

  handleOrgChange = (orgUuid: ?string) => {
    const { potentialOrgs } = this.props;
    const selectedOrg = potentialOrgs.find(org => org.uuid === orgUuid);
    if (!selectedOrg) {
      console.warn({ potentialOrgs, orgUuid });
      console.error('AdminNewAutoMessageCompose - tried to select a nonexistent org');
      ToastActions.showErrorToast(
        'Oops! Looks like something went wrong. Try closing the window and starting over.'
      );
      return;
    }

    this.setState({ orgUuidFromField: orgUuid });

    if (selectedOrg.type === 'District') {
      this.handleSenderChange({ uuid: selectedOrg.uuid, type: 'district' });
    } else {
      this.handleSenderChange(undefined);
    }

    this.props.onChangeField();
  };

  handleSenderChange = (sender: ?AutoMessageSenderInput) => {
    this.setState({ senderFromField: sender });
    this.props.onChangeField();
  };

  handleIncludeParentsChange = (includeParents: boolean) => {
    this.setState({ includeParentsFromField: includeParents });
    this.props.onChangeField();
  };

  handleIncludeStudentsChange = (includeStudents: boolean) => {
    this.setState({ includeStudentsFromField: includeStudents });
    this.props.onChangeField();
  };

  handleMessageContentChange = (messageContent: ?AutoMessageContentInput) => {
    this.setState({ messageContentFromField: messageContent });
    this.props.onChangeField();
  };

  renderDistrictSender = (org: AdminNewAutoMessageOrgs_organizations) => {
    return (
      <Field>
        <Label>Sender</Label>
        <MediaObject gutter="small" center>
          <Avatar
            image="https://remind.imgix.net/0b34f158-c3d0-4321-8cd7-aa6ec744e6d6/school.svg"
            size="small"
          />
          <Text size="xsmall" element="div">
            <Text element="div" weight="semibold">
              {org.name}
            </Text>
            <Text color="thunder">
              Messages at the district level must be sent from the district, and recipients won’t be
              able to reply to this message.
            </Text>
          </Text>
        </MediaObject>
      </Field>
    );
  };

  renderIndividualSenderTypeahead = (admins: *) => {
    const adminOptions = admins.map(
      admin => (admin ? { label: admin.name, value: admin.uuid } : null)
    );
    const { senderFromField } = this.state;

    return (
      <FormElement
        label="Sender"
        element={Select}
        validate={value => assertions.notEmpty(value, 'Please select an administrator')}
        onValidChange={value => this.handleSenderChange({ uuid: value, type: 'user' })}
        onInvalidChange={() => this.handleSenderChange(undefined)}
        subtext="This message will be sent with the administrator’s name. Recipients can reply to this message if the chosen administrator has replies enabled in their account settings."
        elementProps={{
          value: senderFromField && senderFromField.uuid,
          placeholder: 'Choose an administrator',
          name: 'sender',
          options: adminOptions.filter(option => option),
          searchable: true,
        }}
      />
    );
  };

  render() {
    const { onSave, potentialOrgs } = this.props;
    const {
      messageNameFromField,
      orgUuidFromField,
      includeStudentsFromField,
      includeParentsFromField,
      messageContentFromField,
      senderFromField,
    } = this.state;

    const orgOptions = potentialOrgs.map(org => {
      return { label: org.name, value: org.uuid };
    });
    const selectedOrg = potentialOrgs.find(org => org.uuid === orgUuidFromField);

    return (
      <WizardPageLayout
        backButton={
          <Button level="secondary" onClick={this.props.onBack}>
            Back
          </Button>
        }
        actionButtons={[
          <Button
            data-test="continue-button"
            onClick={() =>
              onSave(
                {
                  messageName: messageNameFromField,
                  orgUuid: orgUuidFromField,
                  includeParents: includeParentsFromField,
                  includeStudents: includeStudentsFromField,
                  messageContent: messageContentFromField,
                  sender: senderFromField,
                },
                this.props.onAdvance
              )
            }
            disabled={
              !messageNameFromField ||
              !orgUuidFromField ||
              !messageContentFromField ||
              (!includeParentsFromField && !includeStudentsFromField) ||
              !senderFromField
            }>
            Save and continue
          </Button>,
        ]}>
        <View>
          <Row size="xlarge">
            <Container flush size="large">
              <Text align="left" lineHeight="large">
                <Row size="small">
                  <Heading level="2">
                    <Text weight="semibold">Compose auto message</Text>
                  </Heading>
                </Row>
                <Container flush size="medium" align="left">
                  <FormElement
                    showErrorBeforeBlur
                    label="Message name"
                    onValidChange={e => this.handleMessageNameChange(e.target.value)}
                    onInvalidChange={() => this.handleMessageNameChange(undefined)}
                    subtext="Give this auto message a name that is easily distinguishable. Message names are only
                visible to you and other administrators."
                    validate={e => {
                      assertions.lengthLesserThanOrEqualTo(
                        e.target.value,
                        25,
                        'Please provide a message name under 25 characters long.'
                      );
                    }}
                    element={Input}
                    elementProps={{
                      defaultValue: messageNameFromField,
                      placeholder:
                        '1st Period Attendance, Weekly Lunch Money Balance, Overdue Library Books',
                      name: 'messageName',
                    }}
                  />
                  <FormElement
                    label="Organization"
                    validate={value => assertions.notEmpty(value, 'Please select an organization')}
                    onValidChange={value => this.handleOrgChange(value)}
                    onInvalidChange={() => this.handleOrgChange(undefined)}
                    subtext="Recipients must be a member of this organization to receive this auto message."
                    element={Select}
                    elementProps={{
                      value: orgUuidFromField,
                      placeholder: 'Choose a school or district',
                      name: 'org',
                      options: orgOptions,
                    }}
                  />
                  {selectedOrg && selectedOrg.type === 'District'
                    ? this.renderDistrictSender(selectedOrg)
                    : this.renderIndividualSenderTypeahead(selectedOrg ? selectedOrg.admins : [])}
                  <Field>
                    <Label>Message recipients</Label>
                    <Checkbox
                      onChange={() => this.handleIncludeStudentsChange(!includeStudentsFromField)}
                      label="Students"
                      checked={includeStudentsFromField}
                    />
                    <Checkbox
                      onChange={() => this.handleIncludeParentsChange(!includeParentsFromField)}
                      label="Parents and Guardians"
                      checked={includeParentsFromField}
                    />
                  </Field>
                  <FormElement
                    showErrorBeforeBlur
                    label="Message content"
                    onValidChange={e => this.handleMessageContentChange({ body: e.target.value })}
                    onInvalidChange={() => this.handleMessageContentChange(undefined)}
                    subtext="Recipients will receive this auto message as text messages, push notifications, or emails, depending on their Remind notification preferences."
                    validate={e => {
                      assertions.notEmpty(e.target.value, 'Messages must not be empty.');
                      assertions.lengthLesserThanOrEqualTo(
                        e.target.value,
                        600,
                        'Messages must be 600 characters or less.'
                      );
                    }}
                    element={Textarea}
                    elementProps={{
                      uncontrolled: true,
                      defaultValue: messageContentFromField && messageContentFromField.body,
                      placeholder: 'Type your message',
                    }}
                  />
                  {selectedOrg && selectedOrg.studentIdPercentage < 0.5 ? (
                    <TextWithGlyph
                      center={false}
                      glyph="caution"
                      glyphColor="sunrise"
                      size="xsmall">
                      {`${selectedOrg.studentIdPercentage * 100}% of students at ${
                        selectedOrg.name
                      } have student IDs on Remind. You’ll only be able to message students with student IDs. `}
                      <Link href="#">Learn more here</Link>
                    </TextWithGlyph>
                  ) : null}
                </Container>
              </Text>
            </Container>
          </Row>
        </View>
      </WizardPageLayout>
    );
  }
}

export default AdminNewAutoMessageCompose;
